/* eslint-disable no-nested-ternary  */
import { Dispatch, FC, Fragment, memo, SetStateAction, useEffect } from 'react';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import { useMutation } from '@apollo/client';
import clsx from 'clsx';
import { useRouter } from 'next/router';

import {
  createUrlWithSourceQueryParams,
  useAmplitude,
} from '@crehana/analytics';
import {
  ArrowIosDownOutline,
  ArrowIosUpOutline,
  CloseSmallOutline,
  MenuOutline,
  Phone,
} from '@crehana/icons';
import { useABTest } from '@crehana/react-hooks';
import { Loader, PrimaryButton, TextField } from '@crehana/ui';
import { primaryMain, white } from '@crehana/ui/dist/styles/colors';

import { NUMBERS_ONLY } from '@/views/B2BLanding/constants';
import { AMPLITUDE_TRACKS, EVENTS } from '@/views/B2BLanding/events';

import { BaseMainMenuTypes } from '../../Header.models';
import useHeaderTranslation from '../../hooks/useHeaderTranslation';
import CREATE_REQUEST_DEMO_PHONE_NUMBER from './graphql/CreateRequestDemoPhoneNumber.v4.b2b.graphql';
import {
  CreateRequestDemoPhoneNumber,
  CreateRequestDemoPhoneNumberVariables,
} from './graphql/types/CreateRequestDemoPhoneNumber';

interface PhoneForm {
  phone: string;
}
interface RightProps extends BaseMainMenuTypes {
  darkMode: boolean;
  toggleOpenMobile: () => void;
  setOpenConfirmationDialog: Dispatch<SetStateAction<boolean>>;
}

const Right: FC<React.PropsWithChildren<RightProps>> = ({
  darkMode = false,
  items,
  itemOpen,
  toggleItem,
  showCallButton,
  toggleOpenMobile,
  isMobileOpen,
  setOpenContactDialog,
  setOpenConfirmationDialog,
}) => {
  const router = useRouter();
  const { logEvent } = useAmplitude();
  const { ABVariation: B2BLandingHeaderPhoneInput } = useABTest<'1' | '2'>(
    '176',
  );
  const { t } = useHeaderTranslation();

  const [createRequestDemoPhoneNumber] = useMutation<
    CreateRequestDemoPhoneNumber,
    CreateRequestDemoPhoneNumberVariables
  >(CREATE_REQUEST_DEMO_PHONE_NUMBER, {
    context: { clientName: 'v4.b2b' },
  });

  useEffect(() => {
    // @ts-ignore
    window.VWO = window.VWO || [];
    // @ts-ignore
    window._vis_opt_queue = window._vis_opt_queue || [];
  }, []);

  const {
    handleSubmit,
    register,
    reset,
    formState: { isSubmitting, isValid },
  } = useForm<{ phone: number }>({
    mode: 'onChange',
  });

  const onSubmit = async (formData: PhoneForm) => {
    const { phone } = formData;

    try {
      const { data } = await createRequestDemoPhoneNumber({
        variables: {
          createRequestDemoPhoneNumberInput: {
            requestDemo: {
              phoneNumber: phone,
            },
          },
        },
      });

      if (data?.createRequestDemoPhoneNumber?.success) {
        setOpenConfirmationDialog(true);
        // @ts-ignore
        window._vis_opt_queue.push(() => {
          // @ts-ignore
          window._vis_opt_goal_conversion(214);
        });
        reset();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const getFreeTrialUrl = () => {
    if (typeof window !== 'undefined') {
      const splittedUrl = window.location.href.split(/\?(.+)/);
      const urlRoot = '/business/prueba-gratis/';
      const urlQueryParams = splittedUrl[1];

      return createUrlWithSourceQueryParams({
        url: `${urlRoot}?${urlQueryParams ? `${urlQueryParams}` : ''}`,
        source: `Header - Prueba Gratis Button`,
      });
    }
    return '';
  };

  return (
    <Fragment>
      <div className="items-center hidden lg:flex">
        {items.map(item => {
          const isOpen = itemOpen === item.key;
          const isCollapsible = item.type === 'items' || item.type === 'groups';

          return (
            <div key={item.id}>
              <button
                type="button"
                className={clsx(
                  'font-link font-extrabold text-14 mx-16 px-8 flex items-center cursor-pointer',
                  isOpen && isCollapsible
                    ? 'text-primary-main'
                    : darkMode
                    ? 'text-white'
                    : 'text-base-main',
                )}
                onClick={() => {
                  if (!item.data.link) {
                    toggleItem(item.key);
                  } else {
                    logEvent({
                      event: EVENTS[item.key as keyof typeof EVENTS],
                      data: {},
                    });
                    router.push(item.data.link);
                  }
                }}
                aria-label={t(item.name)}
              >
                {t(item.name)}
                {isCollapsible &&
                  (isOpen ? (
                    <ArrowIosUpOutline
                      className="ml-4"
                      color={primaryMain}
                      size={16}
                    />
                  ) : (
                    <ArrowIosDownOutline
                      color={darkMode ? white : undefined}
                      className="ml-4"
                      size={16}
                    />
                  ))}
              </button>
            </div>
          );
        })}
        {showCallButton ? (
          <PrimaryButton
            leftIcon={{
              component: Phone,
              size: 24,
            }}
            onClick={() => {
              logEvent({
                event: AMPLITUDE_TRACKS.MAIN_HEADER_CALL_US,
                data: {
                  'CTA Type': 'button',
                  'CTA Location': 'header',
                  'CTA Text': t('HEADER_CALL_BUTTON'),
                },
              });
              setOpenContactDialog(true);
            }}
            label={t('HEADER_CALL_BUTTON')}
            className="mx-8"
            size="s"
          />
        ) : (
          <Fragment>
            {B2BLandingHeaderPhoneInput === '1' ? (
              <PrimaryButton
                white={darkMode}
                onClick={() => {
                  logEvent({
                    event: AMPLITUDE_TRACKS.WANTS_TO_KNOW_MORE,
                    data: {
                      'CTA Type': 'button',
                      'CTA Location': 'header',
                      'CTA Text': t('HEADER_FREE_TRIAL_BUTTON'),
                    },
                  });
                  router.push(getFreeTrialUrl());
                }}
                label={t('HEADER_FREE_TRIAL_BUTTON')}
                className="mx-8"
                size="s"
              />
            ) : (
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex items-center"
              >
                <TextField
                  hint={t('HEADER_FORM_PHONE_LABEL')}
                  chat
                  className="mr-8"
                  name="phone"
                  type="tel"
                  maxLength={15}
                  ref={register({
                    required: true,
                    minLength: 8,
                    maxLength: 15,
                    pattern: {
                      value: NUMBERS_ONLY,
                      message: t('HEADER_FORM_PHONE_ERROR'),
                    },
                  })}
                  disabled={isSubmitting}
                  startAdornment={
                    isSubmitting ? <Loader size={16} /> : undefined
                  }
                />
                <PrimaryButton
                  label={t('HEADER_FORM_PHONE_BUTTON')}
                  size="s"
                  type="submit"
                  disabled={isSubmitting || !isValid}
                />
              </form>
            )}
          </Fragment>
        )}
      </div>
      <div className="block lg:hidden">
        <button
          type="button"
          onClick={() => {
            toggleOpenMobile();
          }}
          aria-label="toggle-menu"
        >
          {isMobileOpen ? (
            <CloseSmallOutline color={darkMode ? white : undefined} />
          ) : (
            <MenuOutline color={darkMode ? white : undefined} />
          )}
        </button>
      </div>
    </Fragment>
  );
};

export default memo(Right);
